import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type EssayQuestion } from '../api.ts';
import { ErrorNotice, Loading } from '../components/Bits.tsx';

export function Essays() {
  const queryClient = useQueryClient();
  const { data, isPending, error } = useQuery({
    queryKey: ['essays'],
    queryFn: () => api.get<{ essays: EssayQuestion[] }>('/api/application/essays'),
  });

  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [savedId, setSavedId] = useState<number | null>(null);

  // Seed the editors once the questions arrive, without clobbering anything already typed.
  useEffect(() => {
    if (!data) return;
    setDrafts((current) => {
      const next = { ...current };
      for (const essay of data.essays) {
        if (next[essay.id] === undefined) next[essay.id] = essay.answer ?? '';
      }
      return next;
    });
  }, [data]);

  const save = useMutation({
    mutationFn: (essay: EssayQuestion) =>
      api.put(`/api/application/essays/${essay.id}`, { answer: drafts[essay.id] ?? '' }),
    onSuccess: (_result, essay) => {
      setSavedId(essay.id);
      queryClient.invalidateQueries({ queryKey: ['essays'] });
      queryClient.invalidateQueries({ queryKey: ['application'] });
    },
  });

  if (isPending) return <main id="main"><Loading /></main>;
  if (error) return <main id="main"><ErrorNotice error={error} /></main>;

  const essays = data?.essays ?? [];

  return (
    <main id="main">
      <p><Link to="/apply">← Back to your application</Link></p>
      <h2>Essays</h2>
      <p className="lede">Answer each question in your own words. Save as you go.</p>

      <ErrorNotice error={save.error} />

      {essays.length === 0 && (
        <p className="empty">No questions have been set for this year yet.</p>
      )}

      {essays.map((essay) => {
        const draft = drafts[essay.id] ?? '';
        const over = draft.length > essay.max_length;
        return (
          <form
            key={essay.id}
            className="panel"
            onSubmit={(e) => { e.preventDefault(); save.mutate(essay); }}
          >
            <p style={{ marginTop: 0, fontWeight: 600 }}>{essay.question}</p>
            <textarea
              value={draft}
              aria-label={essay.question}
              onChange={(e) => {
                setSavedId(null);
                setDrafts({ ...drafts, [essay.id]: e.target.value });
              }}
            />
            {/* Counted live, because the server rejects anything over the limit and finding
                that out only on save would mean losing the overflow. */}
            <p className={`counter ${over ? 'over' : ''}`}>
              {draft.length.toLocaleString()} / {essay.max_length.toLocaleString()} characters
              {over && ' — too long to save'}
            </p>
            <div className="actions">
              <button type="submit" disabled={save.isPending || over}>
                {save.isPending ? 'Saving…' : 'Save answer'}
              </button>
              {savedId === essay.id && <span className="hint">Saved.</span>}
            </div>
          </form>
        );
      })}
    </main>
  );
}
